import { useState, useEffect, useCallback } from "react";
import { Clock, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, Send, User, BookOpen, XCircle, ShieldCheck, RefreshCw } from "lucide-react";
import { mockExam } from "../data/mockData";
import { useExamTimer } from "../hooks/useExamTimer";
import { useAntiCheat } from "../hooks/useAntiCheat";
import api from "../services/api";

/* ─── Utility ─── */
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

/* ─── Agreement Screen ─── */
const AgreementScreen = ({ onAccept }) => {
  const [agreed, setAgreed] = useState(false);
  const rules = [
    "ห้ามสลับแท็บหรือเปิดหน้าต่างเบราว์เซอร์ใหม่ในระหว่างการสอบ",
    "ห้ามบันทึกภาพหน้าจอ (Screenshot) ข้อสอบหรือตัวเลือก",
    "ห้ามออกจากหน้าต่างสอบ หรือคลิกนอกหน้าต่างการสอบ",
    "ห้ามคัดลอก (Copy) ข้อความหรือคำถามใดๆ ในข้อสอบ",
    "หากฝ่าฝืนครบ 2 ครั้ง ระบบจะส่งข้อสอบอัตโนมัติทันที ถือว่าสอบเสร็จสิ้น",
  ];
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative"
      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1708885819701-d3be23e34516?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400')", backgroundSize: "cover", backgroundPosition: "center", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#007CB7] rounded-xl flex items-center justify-center mx-auto mb-3"><ShieldCheck size={28} color="white" strokeWidth={1.5} /></div>
          <h1 className="text-xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>ข้อตกลงและเงื่อนไขการสอบออนไลน์</h1>
          <p className="text-sm text-[#5E636E]">กรุณาอ่านและยอมรับก่อนเริ่มสอบ</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
            <p className="font-semibold text-amber-800 flex items-center gap-2 text-sm"><AlertTriangle size={16} strokeWidth={1.5} />ข้อกำหนดการสอบ</p>
            {rules.map((rule) => (
              <div key={rule} className="flex items-start gap-2">
            <span className="text-amber-600 font-bold text-xs mt-0.5 flex-shrink-0">{rules.indexOf(rule) + 1}.</span>
                <p className="text-sm text-amber-800">{rule}</p>
              </div>
            ))}
          </div>
          <label data-testid="agreement-checkbox-label" className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border-2 border-transparent hover:border-[#007CB7] hover:bg-blue-50 transition-all">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="agreement-checkbox" className="mt-0.5 w-5 h-5 rounded text-[#007CB7] border-gray-300 flex-shrink-0" />
            <p className="text-sm text-[#1A1B1F] leading-relaxed">
              <strong>ข้าพเจ้าได้อ่านและเข้าใจข้อกำหนดการสอบทั้งหมดแล้ว</strong> และยินยอมปฏิบัติตามอย่างเคร่งครัด หากฝ่าฝืน ระบบจะส่งข้อสอบอัตโนมัติโดยถือว่าสอบเสร็จสิ้น
            </p>
          </label>
          <button data-testid="agreement-continue-btn" disabled={!agreed} onClick={onAccept}
            className={`w-full py-3 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${agreed ? "bg-[#007CB7] hover:bg-[#005F8C] text-white shadow-sm" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
            <CheckCircle size={16} strokeWidth={1.5} />ยอมรับและดำเนินการต่อ
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Exam Selection Screen ─── */
const ExamSelectScreen = ({ savedExams, onSelect }) => {
  const openExams = savedExams.filter(e => e.status === "เปิดสอบ");
  const bgImage = "url('https://images.unsplash.com/photo-1708885819701-d3be23e34516?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400')";
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 relative"
      style={{ backgroundImage: bgImage, backgroundSize: "cover", backgroundPosition: "center", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#007CB7] rounded-xl flex items-center justify-center mx-auto mb-3">
            <BookOpen size={28} color="white" strokeWidth={1.5} />
          </div>
          <h1 className="text-xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>เลือกวิชาที่ต้องการสอบ</h1>
          <p className="text-sm text-[#5E636E]">รายการข้อสอบที่เปิดให้เข้าสอบในขณะนี้</p>
        </div>
        {openExams.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-10 text-center">
            <Clock size={36} strokeWidth={1} className="text-gray-300 mx-auto mb-3" />
            <p className="font-medium text-[#1A1B1F]">ไม่มีวิชาที่เปิดสอบในขณะนี้</p>
            <p className="text-sm text-[#5E636E] mt-1">กรุณารอการประกาศจากอาจารย์ผู้สอน</p>
          </div>
        ) : (
          <div className="space-y-3">
            {openExams.map(exam => (
              <div key={exam.id} data-testid={`exam-card-${exam.id}`}
                className="bg-white border-2 border-gray-200 hover:border-[#007CB7] rounded-lg shadow-sm p-5 cursor-pointer transition-all group"
                onClick={() => onSelect(exam)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold mb-2">เปิดสอบ</span>
                    <h2 className="font-semibold text-[#1A1B1F] text-base group-hover:text-[#007CB7] transition-colors" style={{ fontFamily: "'Prompt', sans-serif" }}>{exam.title}</h2>
                    <p className="text-sm text-[#5E636E] mt-0.5">{exam.courseName || exam.course}</p>
                    <div className="flex gap-4 mt-2 text-xs text-[#5E636E]">
                      <span className="flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{exam.duration} นาที</span>
                      <span className="flex items-center gap-1"><CheckCircle size={11} strokeWidth={1.5} />{exam.questions?.length} ข้อ</span>
                      <span className="flex items-center gap-1"><BookOpen size={11} strokeWidth={1.5} />{exam.totalScore} คะแนน</span>
                    </div>
                  </div>
                  <button className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors flex-shrink-0">
                    เลือกวิชานี้
                    <ChevronRight size={15} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Pre-Exam Form Screen ─── */
const PreExamScreen = ({ exam, onStart }) => {
  const [form, setForm] = useState({
    studentId: "",
    prefix: "นาย",
    firstName: "",
    lastName: "",
    year: "",
  });

  const isValid = form.studentId.trim() && form.firstName.trim() && form.lastName.trim() && form.year;

  const handleChange = (field, val) => setForm((p) => ({ ...p, [field]: val }));

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 relative"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1708885819701-d3be23e34516?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        fontFamily: "'Noto Sans Thai', sans-serif",
      }}
    >
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#007CB7] rounded-xl flex items-center justify-center mx-auto mb-3">
            <BookOpen size={28} color="white" strokeWidth={1.5} />
          </div>
          <h1
            className="text-xl font-semibold text-[#1A1B1F]"
            style={{ fontFamily: "'Prompt', sans-serif" }}
          >
            วิทยาลัยนวัตกรรมสื่อสารสังคม มศว
          </h1>
          <p className="text-sm text-[#5E636E]">ระบบสอบออนไลน์</p>
        </div>

        {/* Exam Info Card */}
        <div className="bg-[#E6F2F8] border border-blue-200 rounded-lg px-5 py-4 mb-5">
          <p className="font-semibold text-[#1A1B1F] text-base" style={{ fontFamily: "'Prompt', sans-serif" }}>
            {exam?.title || mockExam.title}
          </p>
          <p className="text-sm text-[#5E636E] mt-0.5">{exam?.courseName || exam?.course || mockExam.course}</p>
          <div className="flex gap-4 mt-3 text-xs text-[#5E636E]">
            <span className="flex items-center gap-1"><Clock size={12} strokeWidth={1.5} />{exam?.duration || mockExam.duration} นาที</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} strokeWidth={1.5} />{exam?.questions?.length || mockExam.questions.length} ข้อ</span>
            <span className="flex items-center gap-1"><BookOpen size={12} strokeWidth={1.5} />{exam?.totalScore || mockExam.totalScore} คะแนน</span>
          </div>
        </div>

        {/* Student Info Form */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
          <h2
            className="font-semibold text-[#1A1B1F] flex items-center gap-2"
            style={{ fontFamily: "'Prompt', sans-serif" }}
          >
            <User size={16} strokeWidth={1.5} className="text-[#007CB7]" />
            ข้อมูลผู้เข้าสอบ
          </h2>

          <div>
            <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">
              รหัสนิสิต <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="student-id-input"
              type="text"
              value={form.studentId}
              onChange={(e) => handleChange("studentId", e.target.value)}
              placeholder="เช่น 64010001"
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">
                คำนำหน้า <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="student-prefix-select"
                value={form.prefix}
                onChange={(e) => handleChange("prefix", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none"
              >
                <option value="นาย">นาย</option>
                <option value="นาง">นาง</option>
                <option value="นางสาว">นางสาว</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">
                ชื่อ <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="student-firstname-input"
                type="text"
                value={form.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                placeholder="ชื่อ"
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">
                นามสกุล <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="student-lastname-input"
                type="text"
                value={form.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                placeholder="นามสกุล"
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">
              ชั้นปี <span className="text-red-500">*</span>
            </label>
            <select
              data-testid="student-year-select"
              value={form.year}
              onChange={(e) => handleChange("year", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none"
            >
              <option value="">-- เลือกชั้นปี --</option>
              <option value="1">ชั้นปีที่ 1</option>
              <option value="2">ชั้นปีที่ 2</option>
              <option value="3">ชั้นปีที่ 3</option>
              <option value="4">ชั้นปีที่ 4</option>
            </select>
          </div>

          <button
            data-testid="start-exam-btn"
            disabled={!isValid}
            onClick={() => onStart(form)}
            className={`w-full py-3 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
              isValid
                ? "bg-[#007CB7] hover:bg-[#005F8C] text-white shadow-sm cursor-pointer"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <CheckCircle size={16} strokeWidth={1.5} />
            เริ่มทำข้อสอบ
          </button>

          {!isValid && (
            <p className="text-xs text-center text-[#8A92A3]">
              กรุณากรอกข้อมูลให้ครบถ้วนก่อนเริ่มสอบ
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Submitted / Results Screen ─── */
const SubmittedScreen = ({ studentInfo, answers, submitReason, onReset, overrideGranted, onResume, currentExam }) => {
  const isForceSubmit = submitReason === "cheat";
  const isTimeUp = submitReason === "timeout";

  // Compute from dynamic exam — handles both string and object option formats
  const mcQ = currentExam.questions.filter(q => q.type === "ปรนัย");
  const essayQ = currentExam.questions.filter(q => q.type === "อัตนัย");

  const mcScore = mcQ.reduce((sum, q) => {
    const qIdx = currentExam.questions.indexOf(q);
    // Handle both {id,isCorrect} objects and string arrays (use correctOption index)
    const correctId = typeof q.options[0] === "object"
      ? q.options.find(o => o.isCorrect)?.id
      : String.fromCharCode(97 + (q.correctOption ?? 0));
    return (correctId && answers[qIdx] === correctId) ? sum + q.score : sum;
  }, 0);

  const maxMcScore = mcQ.reduce((s, q) => s + q.score, 0);
  const maxEssayScore = essayQ.reduce((s, q) => s + q.score, 0);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F4F5F6]"
      style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}
    >
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center space-y-5 fade-in-up">
          {isForceSubmit ? (
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle size={32} className="text-red-500" strokeWidth={1.5} />
            </div>
          ) : (
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={32} className="text-green-500" strokeWidth={1.5} />
            </div>
          )}

          <div>
            <h2 className="text-xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>
              {isForceSubmit ? "ส่งข้อสอบอัตโนมัติ" : isTimeUp ? "หมดเวลาสอบ" : "ส่งข้อสอบสำเร็จ"}
            </h2>
            {isForceSubmit && (
              <p className="text-sm text-red-600 mt-1">ระบบตรวจพบการออกจากหน้าต่างสอบ — ข้อสอบถูกส่งโดยอัตโนมัติ</p>
            )}
            {isTimeUp && (
              <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
                <p className="text-amber-800 font-semibold text-sm">ระบบได้ส่งคำตอบอัตโนมัติ</p>
                <p className="text-amber-700 text-xs mt-0.5">เนื่องจากหมดเวลาในการทำข้อสอบ — คำตอบทั้งหมดถูกบันทึกเรียบร้อยแล้ว</p>
              </div>
            )}
          </div>

          {isForceSubmit && (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg px-5 py-4">
              <p className="text-red-700 font-bold text-base text-center" data-testid="petition-text">
                ยื่นคำร้องขอแก้ไข
              </p>
              <p className="text-red-600 text-sm text-center mt-1">
                กรุณาติดต่ออาจารย์เพื่อขอปลดล็อคการสอบ
              </p>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#5E636E]">ผู้เข้าสอบ:</span>
              <span className="font-medium text-[#1A1B1F]">
                {studentInfo.prefix}{studentInfo.firstName} {studentInfo.lastName}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5E636E]">รหัสนิสิต:</span>
              <span className="font-mono font-medium text-[#007CB7]">{studentInfo.studentId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5E636E]">ข้อสอบ:</span>
              <span className="font-medium text-[#1A1B1F] text-right max-w-[200px]">{currentExam.title}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-xs text-[#5E636E] mb-1">คะแนนปรนัย</p>
              <p
                className="text-2xl font-bold text-[#007CB7]"
                style={{ fontFamily: "'Prompt', sans-serif" }}
              >
                {mcScore}/{maxMcScore}
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <p className="text-xs text-[#5E636E] mb-1">อัตนัย</p>
              <p
                className="text-2xl font-bold text-amber-600"
                style={{ fontFamily: "'Prompt', sans-serif" }}
              >
                0/{maxEssayScore}
              </p>
              <p className="text-xs text-[#8A92A3]">รอตรวจ</p>
            </div>
          </div>

          <p className="text-xs text-[#8A92A3]">
            คะแนนอัตนัยจะประกาศหลังอาจารย์ตรวจแล้ว
          </p>

          {/* Override / Resume Section */}
          {isForceSubmit && (
            <div className={`rounded-lg p-4 border text-sm ${overrideGranted ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
              {overrideGranted ? (
                <div className="space-y-2">
                  <p className="font-semibold text-green-700 flex items-center gap-2">
                    <CheckCircle size={16} strokeWidth={1.5} />อาจารย์อนุญาตให้ทำข้อสอบต่อแล้ว
                  </p>
                  <button data-testid="resume-exam-btn" onClick={onResume}
                    className="w-full flex items-center justify-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white py-2.5 rounded-md text-sm font-semibold transition-colors">
                    <RefreshCw size={15} strokeWidth={1.5} />กลับไปทำข้อสอบต่อ
                  </button>
                </div>
              ) : (
                <p className="text-amber-700 flex items-center gap-2">
                  <Clock size={14} strokeWidth={1.5} />รอการอนุญาตจากอาจารย์... (อาจารย์กดปุ่ม "อนุญาตให้ทำต่อ" ในแท็บอาจารย์ผู้สอน)
                </p>
              )}
            </div>
          )}

          <button
            data-testid="back-to-preexam-btn"
            onClick={onReset}
            className="w-full py-2.5 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-gray-50 transition-colors"
          >
            กลับสู่หน้าหลัก
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main StudentView ─── */
export default function StudentView({ savedExams = [], overrideGranted = false, onForceSubmit, onOverrideUsed }) {
  const [phase, setPhase] = useState("agreement");
  const [selectedExam, setSelectedExam] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitReason, setSubmitReason] = useState("manual");
  const [cheatSubmissionId, setCheatSubmissionId] = useState(null);
  const [backendOverrideGranted, setBackendOverrideGranted] = useState(false);

  // Use selected exam or fall back to mockExam for backward compatibility
  const currentExam = selectedExam || mockExam;

  /* ── Timer hook — uses selected exam duration ── */
  const { timeLeft, showFiveMinAlert, dismissFiveMinAlert } = useExamTimer(
    phase === "exam",
    currentExam.duration * 60,
    () => {
      setSubmitReason("timeout");
      setPhase("submitted");
    }
  );

  /* ── Anti-cheat hook (replaces manual ref + event listener effect) ── */
  const { violationCount, resetViolations } = useAntiCheat(phase === "exam", {
    onFirstViolation: () => setShowWarning(true),
    onSecondViolation: () => {
      setSubmitReason("cheat");
      setShowWarning(false);
      setPhase("submitted");
    },
  });

  const handleStart = useCallback((info) => {
    setStudentInfo(info);
    setAnswers({});
    setCurrentQ(0);
    resetViolations();
    setPhase("exam");
  }, [resetViolations]);

  /* ── Notify parent + submit to backend when force-submitted for cheating ── */
  useEffect(() => {
    if (phase === "submitted" && submitReason === "cheat" && studentInfo && onForceSubmit) {
      const info = { studentInfo, answers, timeLeft, currentQ };
      onForceSubmit(info);
      // Auto-submit to backend with needsOverride flag so teacher can see it cross-device
      api.submitExam({
        examId: currentExam?.id || "unknown",
        examTitle: currentExam?.title || "",
        studentId: studentInfo.studentId,
        studentName: `${studentInfo.prefix}${studentInfo.firstName} ${studentInfo.lastName}`,
        studentPrefix: studentInfo.prefix,
        studentYear: parseInt(studentInfo.year),
        answers,
        mcScore: 0,
        submitReason: "cheat",
        needsOverride: true,
        teacherName: currentExam?.teacherName || "",
      }).then(res => {
        // Store submission id for polling override status
        if (res?.data?.id) setCheatSubmissionId(res.data.id);
      }).catch(err => console.error("Cheat submit to backend failed:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, submitReason]);

  /* ── Resume after teacher override ── */
  const handleResume = useCallback(() => {
    resetViolations();
    setShowWarning(false);
    setCheatSubmissionId(null);
    setBackendOverrideGranted(false);
    setPhase("exam");
  }, [resetViolations]);

  /* ── Poll backend for override status (cross-device support) ── */
  useEffect(() => {
    if (phase !== "submitted" || submitReason !== "cheat" || !cheatSubmissionId) return;
    const poll = async () => {
      try {
        const res = await api.getSubmissions();
        const sub = res.data.find(s => s.id === cheatSubmissionId);
        if (sub?.overrideGranted) setBackendOverrideGranted(true);
      } catch (err) { /* silent */ }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [phase, submitReason, cheatSubmissionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => {
    setPhase("agreement");
    setSelectedExam(null);
    setStudentInfo(null);
    setAnswers({});
    setCurrentQ(0);
    resetViolations();
    setShowWarning(false);
    setShowSubmitModal(false);
    setSubmitReason("manual");
    onOverrideUsed && onOverrideUsed();
  };

  const handleAnswer = (qIdx, value) => {
    setAnswers((prev) => ({ ...prev, [qIdx]: value }));
  };

  const handleSubmitConfirm = async () => {
    setShowSubmitModal(false);
    setSubmitReason("manual");
    setPhase("submitted"); // phaseRef removed — using state only via custom hooks
    // Submit to backend (fire and forget — UI already shows submitted)
    try {
      const mcQ = currentExam.questions.filter(q => q.type === "ปรนัย");
      const mcScore = mcQ.reduce((sum, q) => {
        const qIdx = currentExam.questions.indexOf(q);
        const correctId = typeof q.options[0] === "object"
          ? q.options.find(o => o.isCorrect)?.id
          : String.fromCharCode(97 + (q.correctOption ?? 0));
        return (correctId && answers[qIdx] === correctId) ? sum + q.score : sum;
      }, 0);
      await api.submitExam({
        examId: currentExam.id,
        examTitle: currentExam.title,
        studentId: studentInfo.studentId,
        studentName: `${studentInfo.prefix}${studentInfo.firstName} ${studentInfo.lastName}`,
        studentPrefix: studentInfo.prefix,
        studentYear: parseInt(studentInfo.year),
        answers,
        mcScore,
        submitReason: "manual",
      });
    } catch (err) {
      console.error("Submit to backend failed:", err);
    }
  };

  const isCritical = timeLeft <= 60;   // < 1 min → blink red
  const isWarning = timeLeft <= 300 && timeLeft > 60; // 1-5 min → blink orange
  const timerClass = isCritical ? "timer-critical" : isWarning ? "timer-warning" : "timer-normal";
  const answeredCount = Object.keys(answers).length;
  const totalQ = currentExam.questions?.length || 0;
  const currentQuestion = currentExam.questions?.[currentQ];

  // Safety guard: if exam has no questions, go back to select
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F5F6]" style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>
        <div className="bg-white rounded-lg shadow-sm p-8 text-center max-w-sm">
          <p className="text-[#1A1B1F] font-medium mb-3">ข้อสอบนี้ยังไม่มีคำถาม</p>
          <button onClick={() => setPhase("select-exam")} className="bg-[#007CB7] text-white px-4 py-2 rounded-md text-sm">กลับเลือกวิชา</button>
        </div>
      </div>
    );
  }

  /* ── Render: Agreement ── */
  if (phase === "agreement") {
    return <AgreementScreen onAccept={() => { setPhase("select-exam"); }} />;
  }

  /* ── Render: Select Exam ── */
  if (phase === "select-exam") {
    return <ExamSelectScreen savedExams={savedExams} onSelect={(exam) => { setSelectedExam(exam); setPhase("pre-exam"); }} />;
  }

  /* ── Render: Pre-Exam ── */
  if (phase === "pre-exam") {
    return <PreExamScreen exam={currentExam} onStart={handleStart} />;
  }

  /* ── Render: Submitted ── */
  if (phase === "submitted") {
    return (
      <SubmittedScreen
        studentInfo={studentInfo} answers={answers}
        submitReason={submitReason} onReset={handleReset}
        overrideGranted={overrideGranted || backendOverrideGranted}
        onResume={handleResume}
        currentExam={currentExam}
      />
    );
  }

  /* ── Render: Active Exam ── */
  return (
    <div
      className="min-h-screen bg-[#F4F5F6] select-none"
      style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
    >
      {/* Sticky Exam Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p
              className="font-semibold text-[#1A1B1F] text-sm truncate"
              style={{ fontFamily: "'Prompt', sans-serif" }}
            >
              {currentExam.title}
            </p>
            <p className="text-xs text-[#5E636E]">
              {studentInfo?.prefix}{studentInfo?.firstName} {studentInfo?.lastName} — {studentInfo?.studentId}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {violationCount > 0 && (
              <span
                data-testid="violation-badge"
                className="flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full"
              >
                <AlertTriangle size={12} strokeWidth={1.5} />
                คำเตือน {violationCount}/2
              </span>
            )}
            <div
              data-testid="exam-timer"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-mono font-bold text-lg border-2 ${timerClass}`}
            >
              <Clock size={16} strokeWidth={1.5} />
              {formatTime(timeLeft)}
            </div>
          </div>
        </div>
      </div>

      {/* 5-Minute Warning Banner */}
      {showFiveMinAlert && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-40 w-full max-w-lg px-4 fade-in-up" data-testid="five-min-alert">
          <div className="bg-red-600 text-white rounded-lg px-5 py-3 shadow-2xl flex items-center gap-3">
            <AlertTriangle size={20} strokeWidth={1.5} className="flex-shrink-0 animate-pulse" />
            <div className="flex-1">
              <p className="font-bold text-sm">เหลือเวลาอีก 5 นาที!</p>
              <p className="text-xs text-red-200">กรุณาตรวจสอบคำตอบและส่งข้อสอบก่อนหมดเวลา</p>
            </div>
            <button onClick={dismissFiveMinAlert} className="text-red-200 hover:text-white"><XCircle size={18} strokeWidth={1.5} /></button>
          </div>
        </div>
      )}

      {/* Exam Body */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 flex flex-col lg:flex-row gap-6">

        {/* Main Question Area */}
        <div className="flex-1 min-w-0">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 md:p-8">
            {/* Question Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-full bg-[#007CB7] text-white font-bold flex items-center justify-center text-sm flex-shrink-0">
                {currentQ + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#5E636E]">
                  ข้อที่ {currentQ + 1} จาก {totalQ} — ประเภท: {currentQuestion.type} ({currentQuestion.score} คะแนน)
                </p>
              </div>
              <span
                className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                  currentQuestion.type === "ปรนัย"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-purple-100 text-purple-700"
                }`}
              >
                {currentQuestion.type}
              </span>
            </div>

            {/* Question Text */}
            <p
              data-testid="question-text"
              className="text-[#1A1B1F] text-base leading-relaxed mb-6"
            >
              {currentQuestion.text}
            </p>

            {/* MC Options — handles both {id,text,isCorrect} objects AND plain string arrays */}
            {currentQuestion.type === "ปรนัย" && (
              <div className="space-y-3" data-testid="mc-options">
                {currentQuestion.options.map((opt, optIdx) => {
                  // Normalize: teacher-created exams store options as strings
                  const optId = typeof opt === "object" ? opt.id : String.fromCharCode(97 + optIdx);
                  const optText = typeof opt === "object" ? opt.text : opt;
                  return (
                    <label
                      key={optId}
                      data-testid={`option-${optId}`}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[currentQ] === optId
                          ? "border-[#007CB7] bg-[#E6F2F8]"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${currentQ}`}
                        value={optId}
                        checked={answers[currentQ] === optId}
                        onChange={() => handleAnswer(currentQ, optId)}
                        className="mt-0.5 w-4 h-4 text-[#007CB7] border-gray-300 focus:ring-[#007CB7] flex-shrink-0"
                      />
                      <span className="text-[#1A1B1F] text-sm leading-relaxed">
                        <span className="font-semibold text-[#5E636E] mr-2">
                          {optId.toUpperCase()}.
                        </span>
                        {optText}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Essay Answer */}
            {currentQuestion.type === "อัตนัย" && (
              <div data-testid="essay-answer-area">
                <textarea
                  data-testid="essay-textarea"
                  value={answers[currentQ] || ""}
                  onChange={(e) => handleAnswer(currentQ, e.target.value)}
                  placeholder="พิมพ์คำตอบของท่านที่นี่..."
                  rows={8}
                  className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none resize-none text-[#1A1B1F] placeholder-gray-400 select-text"
                  style={{ userSelect: "text" }}
                />
                <p className="text-xs text-[#8A92A3] mt-1">
                  {(answers[currentQ] || "").length} ตัวอักษร
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t border-gray-100">
              <button
                data-testid="prev-question-btn"
                disabled={currentQ === 0}
                onClick={() => setCurrentQ((p) => p - 1)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} strokeWidth={1.5} />
                ข้อก่อนหน้า
              </button>

              <span className="text-xs text-[#8A92A3]">
                ตอบแล้ว {answeredCount}/{totalQ} ข้อ
              </span>

              {currentQ < totalQ - 1 ? (
                <button
                  data-testid="next-question-btn"
                  onClick={() => setCurrentQ((p) => p + 1)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#007CB7] hover:bg-[#005F8C] text-white rounded-md text-sm transition-colors"
                >
                  ข้อถัดไป
                  <ChevronRight size={16} strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  data-testid="final-submit-btn"
                  onClick={() => setShowSubmitModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm transition-colors"
                >
                  <Send size={14} strokeWidth={1.5} />
                  ส่งคำตอบ
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar: Question Grid */}
        <div className="lg:w-60 xl:w-64 flex-shrink-0">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 sticky top-20">
            <h3
              className="text-xs font-semibold text-[#5E636E] uppercase tracking-wide mb-3"
            >
              สถานะข้อสอบ
            </h3>
            <div className="grid grid-cols-5 gap-1.5 mb-4" data-testid="question-status-grid">
              {currentExam.questions.map((q, idx) => (
                <button
                  key={q.id}
                  data-testid={`status-btn-${idx}`}
                  onClick={() => setCurrentQ(idx)}
                  className={`w-full aspect-square rounded text-xs font-semibold transition-all ${
                    currentQ === idx
                      ? "bg-[#007CB7] text-white shadow-sm scale-105"
                      : answers[idx] !== undefined
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-[#5E636E] hover:bg-gray-200"
                  }`}
                  title={`ข้อ ${idx + 1}: ${answers[idx] !== undefined ? "ตอบแล้ว" : "ยังไม่ตอบ"}`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            <div className="space-y-1.5 text-xs text-[#5E636E] mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded bg-[#007CB7]" />
                <span>ข้อปัจจุบัน</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded bg-green-500" />
                <span>ตอบแล้ว ({answeredCount})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded bg-gray-100 border border-gray-300" />
                <span>ยังไม่ตอบ ({totalQ - answeredCount})</span>
              </div>
            </div>

            <button
              data-testid="submit-exam-btn"
              onClick={() => setShowSubmitModal(true)}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-md text-sm font-medium transition-colors"
            >
              <Send size={14} strokeWidth={1.5} />
              ส่งคำตอบ
            </button>
          </div>
        </div>
      </div>

      {/* ─── Anti-Cheat Warning Modal ─── */}
      {showWarning && (
        <div
          data-testid="anticheat-warning-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="bg-white border-2 border-red-500 rounded-xl p-8 max-w-sm w-full shadow-2xl fade-in-up text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-red-500" strokeWidth={1.5} />
            </div>
            <h2
              className="text-xl font-bold text-red-700 mb-2"
              style={{ fontFamily: "'Prompt', sans-serif" }}
            >
              คำเตือน!
            </h2>
            <p className="text-sm text-[#1A1B1F] leading-relaxed mb-2">
              <strong>ห้ามสลับแท็บหรือออกจากหน้าต่างสอบ</strong>
            </p>
            <p className="text-sm text-red-600 mb-6">
              มิฉะนั้นระบบจะส่งข้อสอบอัตโนมัติ
              <br />
              <span className="font-bold">คำเตือนที่ {violationCount}/2</span>
            </p>
            <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-xs text-red-700 mb-5">
              หากออกจากหน้าต่างสอบอีกครั้ง ระบบจะบันทึกและส่งคำตอบทั้งหมดทันที
            </div>
            <button
              data-testid="anticheat-acknowledge-btn"
              onClick={() => setShowWarning(false)}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-md text-sm font-semibold transition-colors"
            >
              รับทราบและกลับสู่การสอบ
            </button>
          </div>
        </div>
      )}

      {/* ─── Submit Confirmation Modal ─── */}
      {showSubmitModal && (
        <div
          data-testid="submit-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-sm w-full shadow-2xl fade-in-up text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send size={24} className="text-[#007CB7]" strokeWidth={1.5} />
            </div>
            <h2
              className="text-lg font-semibold text-[#1A1B1F] mb-2"
              style={{ fontFamily: "'Prompt', sans-serif" }}
            >
              ยืนยันการส่งข้อสอบ
            </h2>
            <p className="text-sm text-[#5E636E] mb-4">
              ตอบแล้ว <strong className="text-[#1A1B1F]">{answeredCount}</strong> จาก{" "}
              <strong className="text-[#1A1B1F]">{totalQ}</strong> ข้อ
            </p>
            {answeredCount < totalQ && (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-xs text-amber-700 mb-4">
                ยังมี {totalQ - answeredCount} ข้อที่ยังไม่ได้ตอบ
                <br />
                เมื่อส่งแล้วจะไม่สามารถแก้ไขคำตอบได้
              </div>
            )}
            <p className="text-xs text-[#8A92A3] mb-6">
              เมื่อยืนยันแล้วจะไม่สามารถแก้ไขคำตอบได้
            </p>
            <div className="flex gap-3">
              <button
                data-testid="submit-modal-cancel-btn"
                onClick={() => setShowSubmitModal(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-gray-50 transition-colors"
              >
                ยกเลิก
              </button>
              <button
                data-testid="submit-modal-confirm-btn"
                onClick={handleSubmitConfirm}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold transition-colors"
              >
                ยืนยันส่งคำตอบ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
